/**
 * Friendly display labels for the technical identifiers the Email Center
 * juggles: Twig variable paths, translation keys and fragment keys. The raw
 * identifier stays available (tooltips, secondary lines) — these helpers only
 * produce the human-readable primary label so non-technical users aren't
 * confronted with `theme.colors.primary` or `partials/xxxl/button`.
 */

export interface FriendlyLabel {
  /** Human-readable primary label ("Event start date"). */
  label: string;
  /** Optional muted context ("Theme · Colors"); empty string when none. */
  context: string;
}

/** "appointment_confirmed" / "startDate" / "email-base" → "Appointment confirmed" etc. */
export function humanizeToken(raw: string): string {
  const words = raw
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  if (!words) return raw;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Dot-path variable ("theme.colors.primary") → label "Primary", context "Theme · Colors". */
export function humanizeVariablePath(path: string): FriendlyLabel {
  const segments = path.split('.').filter(Boolean);
  if (segments.length === 0) return { label: path, context: '' };
  return {
    label: humanizeToken(segments[segments.length - 1]),
    context: segments.slice(0, -1).map(humanizeToken).join(' · '),
  };
}

/** Translation key ("title.appointment_confirmed.kitchen") → label from the key part, context from the group. */
export function humanizeTransKey(fullKey: string): FriendlyLabel {
  const segments = fullKey.split('.').filter(Boolean);
  if (segments.length <= 1) {
    return { label: humanizeToken(fullKey), context: '' };
  }
  return {
    label: humanizeToken(segments.slice(1).join(' ')),
    context: humanizeToken(segments[0]),
  };
}

/**
 * Fragment-key path segments that describe structure, not identity — dropped
 * from friendly fragment names ("general/layout/email_base" → "Email base").
 */
const FRAGMENT_STRUCTURE_SEGMENTS = new Set([
  'layout',
  'layouts',
  'partial',
  'partials',
  'general',
  'fragments',
]);

/** Fragment key ("layout/xxxl/base") → label "Base", context "Xxxl". */
export function humanizeFragmentKey(key: string): FriendlyLabel {
  const segments = key.split('/').filter(Boolean);
  const meaningful = segments.filter(
    (segment) => !FRAGMENT_STRUCTURE_SEGMENTS.has(segment.toLowerCase()),
  );
  const parts = meaningful.length > 0 ? meaningful : segments.slice(-1);
  if (parts.length === 0) return { label: key, context: '' };
  return {
    label: humanizeToken(parts[parts.length - 1]),
    context: parts.slice(0, -1).map(humanizeToken).join(' · '),
  };
}

/**
 * Anything that can name a fragment: the row itself, or just its key for the
 * call sites that only have one (the block registry, a raw `{% include %}`).
 */
export type NamedFragment = { key: string; name?: string | null } | string;

/**
 * What a fragment is called.
 *
 * A partner's own blocks carry a `name` and their key is deliberately opaque
 * (`partials/9f3c1a7be2d54a08`), so humanizing the key would produce a random
 * hex string. The product-managed library has no name and a meaningful key
 * (`partials/xxxl/footer` → "Xxxl · Footer"), so the key stays the label there.
 */
export function humanizeFragment(fragment: NamedFragment): FriendlyLabel {
  if (typeof fragment === 'string') return humanizeFragmentKey(fragment);
  const name = fragment.name?.trim();
  return name
    ? { label: name, context: '' }
    : humanizeFragmentKey(fragment.key);
}

/** Single-line fragment name for select options: "Xxxl · Base". */
export function fragmentDisplayName(fragment: NamedFragment): string {
  const { label, context } = humanizeFragment(fragment);
  return context ? `${context} · ${label}` : label;
}
